import { useEffect, useRef, useState } from "react";
import { modelLabel } from "../../../shared/models";
import { reviewTemplate } from "../../../shared/review-templates";
import type { AgentDTO, AgentInsightsDTO, AgentVersionDTO, ApprovalDTO, ConnectorDTO, CostSplit, MemoryDTO, SkillDTO } from "../../../shared/types";
import { accessCharacter, GOAL_DESCRIPTION_BUDGET, PLATFORM_ASSISTANT_SLUG, type SubjectTypeDTO } from "../../../shared/types";
import { pluralWord } from "../../../shared/scope-words";
import { useScopeTypes } from "../../lib/scopes";
import { PromptPreview } from "../../components/PromptPreview";
import { accessLine, AgentForm, connectorBrand, deleteAgent, NewAgentModal, OverBudget, ownersLine, saveAgent, toDraft, usePickers, type AgentDraft } from "../../components/AgentEditor";
import { AgentAvatar, forgetAgentAvatars } from "../../components/AgentAvatar";
import { AgentScopes } from "../../components/AgentScopes";
import { AgentTriggers } from "../../components/AgentTriggers";
import { KeptFiles } from "../../components/KeptFiles";
import { AgentStatusChip } from "./Agents";
import { BrandIcon } from "../../components/BrandIcon";
import { Icon } from "../../components/Icon";
import { List, ListRow } from "../../components/List";
import { CostBreakdown, RunCost } from "../../components/CostBreakdown";
import { RunsList } from "../../components/RunsList";
import { MoreOnScroll, usePaged } from "../../components/MoreOnScroll";
import { SkillWho } from "../../components/SkillWho";
import { AgentModelList } from "../../components/AgentModelList";
import { Table } from "../../components/Table";
import { Tabs, useTabParam, type TabItem } from "../../components/Tabs";
import { TextLink } from "../../components/TextLink";
import { confirmDialog } from "../../components/Dialog";
import { Avatar, Badge, Button, compact, duration, ErrorText, Field, inputCls, Panel, Sparkline, StatusBadge, SubjectBadge, timeAgo } from "../../components/ui";
import { ApiError, del, get, patch, post, postFile, put } from "../../lib/api";
import { changedBy } from "../../lib/memory";
import { approvedWord, releaseState } from "../../lib/approval-text";
import { Link, navigate } from "../../lib/router";
import { agentStatusWords } from "../../lib/schedules";
import { Meta } from "../RequestList";
import { Switch } from "../../components/Switch";
import { ScheduleDialog, ScheduleList } from "../../components/Schedules";
import { inFiringOrder } from "../../lib/schedules";
import { useUser } from "../../App";

const TABS = ["activity", "goals", "runs", "requests", "connectors", "skills", "scopes", "memory", "schedules", "triggers", "subagents", "settings"] as const;
type Tab = (typeof TABS)[number];
// A `task` proposal is history (the task feature went on 2026-10-19): the decided ones still read.
const KIND_LABEL: Record<ApprovalDTO["kind"], string> = { goal: "Goal", task: "Task (retired)", workflow: "Schedule (old)", schedule: "Schedule", sub_agent: "Sub-agent", question: "Question", note: "Note", skill: "Skill", review: "Request", file_share: "File share", agent_edit: "Instructions", skill_change: "Skill change", schedule_handover: "Schedule handover", access: "Access", action: "Sensitive action" };
const APPROVAL_TONE: Record<ApprovalDTO["status"], "amber" | "emerald" | "zinc" | "blue"> = { pending: "amber", approved: "emerald", rejected: "zinc", answered: "blue", revision_requested: "amber", withdrawn: "zinc" };
const money = (usd: number) => `$${usd.toFixed(2)}`;

function StatCard({ label, value, trend, tone = "text-haze-deep", breakdown }: { label: string; value: string | number; trend?: number[]; tone?: string; breakdown?: CostSplit }) {
  return (
    <div className="card flex min-w-0 flex-col overflow-hidden p-4">
      <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-muted">
        <span className="min-w-0 truncate">{label}</span>
        {breakdown && <CostBreakdown billedUsd={breakdown.billedUsd} estimatedUsd={breakdown.estimatedUsd} />}
      </div>
      <div className="mt-1.5 truncate text-title leading-none font-semibold tracking-tight tabular-nums">{value}</div>
      <div className={`-mx-4 -mb-4 mt-3 ${tone}`}>{trend ? <Sparkline values={trend} /> : <div className="h-8" />}</div>
    </div>
  );
}

function Bars({ rows }: { rows: { label: React.ReactNode; value: number; note?: string }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="min-w-0">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="min-w-0 truncate">{r.label}</span>
            <span className="shrink-0 text-muted tabular-nums">
              {r.value}
              {r.note ? ` · ${r.note}` : ""}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-fill">
            <div className="h-full rounded-full bg-ink/70 dark:bg-white/60" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityTab({ data, connectors }: { data: AgentInsightsDTO; connectors: ConnectorDTO[] }) {
  const brandFor = (slug: string) => {
    const c = connectors.find((x) => x.slug === slug);
    return c ? connectorBrand(c) : slug;
  };
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <List title="Recent runs" empty="No runs yet.">
        {data.recentRuns.map((r) => (
          <ListRow
            key={r.id}
            to={`/admin/runs/${r.id}`}
            title={
              <>
                <StatusBadge status={r.status} />
                {r.parentRunId && <span className="text-tiny text-faint">↳ delegated · {r.mode}</span>}
                {!r.parentRunId && <span className="text-tiny text-faint">{r.mode}</span>}
              </>
            }
            subtitle={r.task.split("\n")[0]}
            trailing={
              <div className="text-right tabular-nums">
                <div className="text-fg"><RunCost usd={r.costUsd} paidBy={r.paidBy} /></div>
                <div className="text-tiny">{r.startedAt ? duration(r.startedAt, r.finishedAt) : timeAgo(r.createdAt)}</div>
              </div>
            }
          />
        ))}
      </List>
      <div className="min-w-0 space-y-6">
        <List title="Hands work to" empty={`No delegations in the last ${data.days} days.`}>
          {data.delegatedTo.map((d) => (
            <ListRow
              key={d.agentId}
              to={`/admin/agents/${d.agentId}`}
              leading={<AgentAvatar agent={d.agentId} name={d.name} size="sm" />}
              title={<span className="min-w-0 truncate text-sm">{d.name}</span>}
              trailing={<><span className="tabular-nums">{d.runs} runs · {money(d.costUsd)}</span><CostBreakdown billedUsd={d.billedUsd} estimatedUsd={d.estimatedUsd} /></>}
            />
          ))}
        </List>
        <List title="Gets work from" empty="Only asked directly.">
          {data.delegatedFrom.map((d) => (
            <ListRow
              key={d.agentId}
              to={`/admin/agents/${d.agentId}`}
              leading={<AgentAvatar agent={d.agentId} name={d.name} size="sm" />}
              title={<span className="min-w-0 truncate text-sm">{d.name}</span>}
              trailing={<span className="tabular-nums">{d.runs} runs</span>}
            />
          ))}
        </List>
        <Panel title="Tool usage">
          {data.tools.length ? <Bars rows={data.tools.map((t) => ({ label: <span className="font-mono">{t.name}</span>, value: t.calls }))} /> : <p className="text-xs text-muted">No tool calls yet.</p>}
        </Panel>
        <Panel title="Connector calls">
          {data.connectors.length ? (
            <Bars
              rows={data.connectors.map((c) => ({
                label: (
                  <span className="inline-flex items-center gap-1.5">
                    <BrandIcon brand={brandFor(c.slug)} className="size-3.5" /> {c.name}
                  </span>
                ),
                value: c.calls,
                note: c.errors ? `${c.errors} errors` : undefined,
              }))}
            />
          ) : (
            <p className="text-xs text-muted">No connector calls yet.</p>
          )}
        </Panel>
      </div>
    </div>
  );
}

/** Pandito's page also carries what every agent knows and what they know about people. */
const MEMORY_LEVEL_LABEL: Record<MemoryDTO["level"], string | null> = {
  agent: null,
  global: "all agents",
  user: "about one person",
  user_agent: "about one person",
  scope: "one scope",
};

function MemoryTab({ data, onChanged }: { data: AgentInsightsDTO; onChanged: () => void }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pinned, setPinned] = useState(false);
  const [level, setLevel] = useState<"agent" | "global">("agent");
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  // The agent's own memory; the primary agent's page also carries global and person memories. A scope's memory is on the scope's page.
  const memories = data.memories;
  // Its shared memory is its owners' to change (D5); everything else on this tab stays any admin's.
  const operates = data.agent.youAnswerFor !== false;
  const sorted = [...memories].sort((a, b) => Number(b.pinned) - Number(a.pinned) || Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0 space-y-6">
        <List
          title={`Persistent memory · ${memories.length}`}
          hint={data.agent.scopeTypes.length > 0 ? "Its own memory, read by every run of it. What it learns about one scope is kept on that scope, for every agent working under it." : undefined}
          empty={
            <>
              Nothing remembered yet. The agent saves what it learns with <code className="font-mono text-xs">pandas memory save --level agent</code>.
            </>
          }
        >
          {sorted.map((m: MemoryDTO) => (
            <ListRow
              key={m.id}
              title={
                <>
                  {m.pinned && <Icon name="pin" className="size-3.5 text-muted" />}
                  <span className="text-sm font-medium">{m.title}</span>
                  <Badge tone={m.source === "agent" ? "blue" : "zinc"}>{m.source === "agent" ? "written by the agent" : m.source}</Badge>
                  {changedBy(m) && <Badge tone="zinc">changed by {changedBy(m)}</Badge>}
                  {MEMORY_LEVEL_LABEL[m.level] && <Badge tone={m.level === "global" ? "blue" : "zinc"}>{MEMORY_LEVEL_LABEL[m.level]}</Badge>}
                </>
              }
              trailing={
                (m.level !== "agent" || operates) && <>
                  <Button variant="ghost" size="sm" onClick={() => run(() => patch(`/api/memories/${m.id}`, { pinned: !m.pinned }))}>
                    {m.pinned ? "Unpin" : "Pin"}
                  </Button>
                  <Button variant="danger" size="sm" onClick={async () => (await confirmDialog({ title: `Delete "${m.title}"?`, body: "The memory is gone for good.", confirm: "Delete memory", danger: true })) && run(() => del(`/api/memories/${m.id}`))}>
                    Delete
                  </Button>
                </>
              }
            >
              <p className="mt-1.5 line-clamp-6 text-sm whitespace-pre-wrap text-muted">{m.content}</p>
              <div className="mt-1.5 text-tiny text-faint">Updated {timeAgo(m.updatedAt)}</div>
            </ListRow>
          ))}
        </List>
        <KeptFiles
          on={{ agentId: data.agent.id }}
          title="Files it keeps"
          hint="Its own, and what its owners keep for it: every run of this agent can read what an owner keeps here."
          canUpload={data.agent.youAnswerFor === true}
        />
      </div>
      <Panel
        title="Add to memory"
        hint="Pinned memories are in every run of this agent; others are listed by title for the agent to look up."
        className="self-start lg:sticky lg:top-4"
      >
        {!operates ? (
          <p className="text-sm text-muted">Its shared memory is for its owners to change. To add to it, add yourself as an owner first, in its settings.</p>
        ) : (
        <div className="space-y-3">
          {data.agent.isPrimary && (
            <Field label="Who remembers this">
              <select className={inputCls} value={level} onChange={(e) => setLevel(e.target.value as "agent" | "global")}>
                <option value="agent">Pandito only</option>
                <option value="global">Every agent</option>
              </select>
            </Field>
          )}
          <Field label="Title">
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Partner codes" />
          </Field>
          <Field label="Content">
            <textarea className={inputCls} rows={6} value={content} onChange={(e) => setContent(e.target.value)} />
          </Field>
          <Switch label="Pin, so every run has it" checked={pinned} onChange={setPinned} />
          <Button
            variant="primary"
            disabled={!title.trim() || !content.trim()}
            onClick={() =>
              run(async () => {
                await post("/api/memories", { level, agentId: level === "agent" ? data.agent.id : null, title, content, pinned, tags: [] });
                setTitle("");
                setContent("");
                setPinned(false);
              })
            }
          >
            Save memory
          </Button>
          <ErrorText error={error} />
        </div>
        )}
      </Panel>
    </div>
  );
}

/**
 * The agent's schedules in one list (AIT-59), the next to fire first, each tagged with who it runs as: a person, or the
 * agent itself, which its owners set up (AIT-56). One New schedule, whose "Runs as" is its owners' choice. "About": the
 * scope a new schedule is about, among the agent's types; the agent then works on it under that scope.
 */
function SchedulesTab({ data, onChanged }: { data: AgentInsightsDTO; onChanged: () => void }) {
  const { agent } = data;
  const [adding, setAdding] = useState(false);
  const owns = agent.youAnswerFor === true;
  // Offered only where the server would take one (D6, AIT-56): an enabled agent you may use, and a specialist only if you own it.
  const canSchedule = agent.enabled && (agent.isPrimary ? agent.youCanUse === true : owns);
  // Its own: never the primary agent (it works for people) or the assistant.
  const takesOwn = !agent.isPrimary && agent.slug !== PLATFORM_ASSISTANT_SLUG;
  return (
    <>
      <ScheduleList
        title={`Schedules · ${data.schedules.length}`}
        hint={`Each runs as the person it belongs to, in their own chat with ${agent.name}${takesOwn ? `, or as ${agent.name} itself, with its workspace keys, where its owners send it` : ""}. Its owners may pause or delete any of them.`}
        action={canSchedule && <Button onClick={() => setAdding(true)}>New schedule</Button>}
        empty={`Nothing scheduled, so ${agent.name} only works when it is asked.`}
        schedules={inFiringOrder(data.schedules)}
        ownsAgent={() => owns}
        runsAsTag
        scopeAgent={agent}
        onChanged={onChanged}
      />
      {adding && <ScheduleDialog schedule={null} agentId={agent.id} agentName={agent.name} scopeAgent={agent} onSaved={onChanged} onClose={() => setAdding(false)} />}
    </>
  );
}

/**
 * An agent's one goal (093): what it works toward, and how anyone outside it knows it is achieved. Its owners set it,
 * change its words in place, mark it achieved or drop it; an approved proposal of the agent's replaces it. Goals that
 * ended are a quiet history below, read only. A goal has no due date: time-bound work is a schedule.
 */
function GoalTab({ data, onChanged }: { data: AgentInsightsDTO; onChanged: () => void }) {
  const current = data.goals.find((g) => g.status === "active" || g.status === "paused") ?? null;
  const history = data.goals.filter((g) => g !== current);
  // An agent's goal is its owners' (D5 amendment 3): anyone else reads it and proposes through the agent.
  const operates = data.agent.youAnswerFor !== false;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      setEditing(false);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const edit = () => {
    setTitle(current?.title ?? "");
    setTarget(current?.target ?? "");
    setDescription(current?.description ?? "");
    setError(null);
    setEditing(true);
  };
  const end = async (status: "achieved" | "dropped") => {
    if (!current) return;
    const ok = await confirmDialog(
      status === "achieved"
        ? { title: `Mark "${current.title}" achieved?`, body: "It moves to the previous goals, and the agent has no goal until a new one is set.", confirm: "Mark achieved" }
        : { title: `Drop "${current.title}"?`, body: "It moves to the previous goals, and the agent has no goal until a new one is set.", confirm: "Drop goal", danger: true },
    );
    if (ok) await run(() => patch(`/api/goals/${current.id}`, { status }));
  };
  const form = operates && (editing || !current);
  const hint = data.agent.isPrimary
    ? "Pandito hands work to specialists, so its runs are not shown a goal."
    : "Every run of this agent reads it, before its instructions.";
  return (
    <div className="max-w-3xl space-y-6">
      <Panel
        title="Goal"
        hint={hint}
        action={current && operates && !editing ? <Button size="sm" onClick={edit}>Change</Button> : undefined}
      >
        {form ? (
          <div className="space-y-3">
            <Field label="Goal">
              <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Every partner on the new codes" />
            </Field>
            <Field label="Achieved when" hint="Measured by something outside the agent. A goal has no due date: time-bound work is a schedule.">
              <input className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="No partner on the old codes in BigQuery" />
            </Field>
            <Field label="Description" hint="The rest of the goal: why it matters, what to do when results come in, when to stop, and the lines it never crosses.">
              <textarea className={inputCls} rows={8} value={description} onChange={(e) => setDescription(e.target.value)} />
              <span className="mt-1 block text-tiny text-faint">{description.length.toLocaleString("en-US")} / {GOAL_DESCRIPTION_BUDGET.toLocaleString("en-US")} characters</span>
              <OverBudget chars={description.length} budget={GOAL_DESCRIPTION_BUDGET} what="Runs read only that much of it: the method belongs in the instructions and skills." />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={busy || !title.trim()}
                onClick={() => run(() => (current ? patch(`/api/goals/${current.id}`, { title, target, description }) : post(`/api/agents/${data.agent.id}/goals`, { title, target, description })))}
              >
                {current ? "Save" : "Set goal"}
              </Button>
              {editing && (
                <Button variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        ) : current ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-heading font-semibold">{current.title}</span>
              {current.status === "paused" && <Badge tone="amber">paused</Badge>}
              {current.createdByType === "agent" && <Badge>proposed by the agent</Badge>}
            </div>
            <p className="text-sm">
              <span className="text-muted">Achieved when: </span>
              {current.target || <span className="text-faint">not said yet</span>}
            </p>
            {current.description && <p className="whitespace-pre-wrap text-sm text-muted">{current.description}</p>}
            <p className="text-xs text-faint">Set {timeAgo(current.createdAt)}</p>
            {operates && (
              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" disabled={busy} onClick={() => end("achieved")}>
                  Mark achieved
                </Button>
                <Button variant="danger" size="sm" disabled={busy} onClick={() => end("dropped")}>
                  Drop
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted">No goal. Its owners set one, or the agent proposes one for them to approve.</p>
        )}
        {!form && !operates && <p className="mt-3 text-xs text-faint">Its goal is for its owners to set. To change it, add yourself as an owner first, in its settings.</p>}
        <ErrorText error={error} />
      </Panel>
      {history.length > 0 && (
        <List title={`Previous goals · ${history.length}`}>
          {history.map((g) => (
            <ListRow
              key={g.id}
              title={
                <>
                  <span className="text-sm text-muted">{g.title}</span>
                  <Badge tone={g.status === "achieved" ? "emerald" : "zinc"}>{g.status}</Badge>
                </>
              }
            >
              {g.target && <p className="mt-1 text-xs text-faint">Achieved when: {g.target}</p>}
              <div className="mt-1 text-tiny text-faint">Ended {timeAgo(g.updatedAt)}</div>
            </ListRow>
          ))}
        </List>
      )}
    </div>
  );
}

/**
 * Everything its runs asked, proposed or filed for a verdict, whoever it went to: what still waits first, then what
 * was decided or withdrawn, newest first. A row opens the request with its card; the chat is the one it was made in.
 */
function RequestsTab({ agent }: { agent: AgentDTO }) {
  const { rows, done, loading, error, more } = usePaged<ApprovalDTO>(
    (before) => get(`/api/agents/${agent.id}/requests${before ? `?before=${before}` : ""}`),
    [agent.id],
  );
  return (
    <div className="space-y-2">
      <Table
        title="Requests"
        hint={`The questions ${agent.name} asked, what it proposed to start on its own, and what it made that needed a verdict.`}
        action={<TextLink to="/admin/approvals" className="text-xs text-muted">All requests</TextLink>}
        rows={rows ?? []}
        rowKey={(a) => a.id}
        onRowClick={(a) => navigate(`/admin/approvals/${a.id}`)}
        empty={rows ? `${agent.name} has not asked or proposed anything yet.` : "Loading…"}
        columns={[
          {
            header: "Request",
            primary: true,
            cell: (a) => (
              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 whitespace-nowrap">
                  <Badge>{(a.template && reviewTemplate(a.template)?.label) ?? KIND_LABEL[a.kind]}</Badge>
                </span>
                <span className="min-w-0 truncate">{a.title}</span>
              </div>
            ),
          },
          {
            header: "Status",
            // Approved but not sent, or maybe sent, is amber, as on the Requests page.
            cell: (a) => {
              const release = releaseState(a);
              return <Badge tone={release === "unsent" || release === "uncertain" ? "amber" : APPROVAL_TONE[a.status]}>{approvedWord(a).toLowerCase()}</Badge>;
            },
          },
          {
            header: "Who and when",
            className: "text-xs text-muted",
            // An answer or a note can be long: two lines at most, so the title keeps its room.
            cell: (a) => (
              <div className="line-clamp-2 whitespace-normal lg:w-72">
                <Meta a={a} />
              </div>
            ),
          },
          {
            header: "Chat",
            className: "text-xs text-muted",
            cell: (a) =>
              a.conversationId && (
                <TextLink to={`/admin/conversations/${a.conversationId}`} onClick={(e) => e.stopPropagation()}>
                  open the chat
                </TextLink>
              ),
          },
        ]}
      />
      <ErrorText error={error} />
      <MoreOnScroll onMore={more} done={done} loading={loading} />
    </div>
  );
}

/** The agents' own skills it has, given to it by name or to every agent. A scope's skills and a person's are not its. */
function SkillsTab({ agent, skills, error }: { agent: AgentDTO; skills: SkillDTO[] | null; error: string | null }) {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  useEffect(() => {
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
  }, []);
  return (
    <div className="space-y-2">
      <Table
        title="Skills"
        hint={`The agents' own skills ${agent.name} has, given to it by name or to every agent. Scope skills live on the scope; people's own skills on their About me.`}
        action={<TextLink to="/admin/skills?level=agent" className="text-xs text-muted">All skills</TextLink>}
        rows={skills ?? []}
        rowKey={(s) => s.id}
        onRowClick={(s) => navigate(`/admin/skills/${s.id}?level=agent`)}
        empty={skills ? `${agent.name} has none of the agents' skills yet. Give it one from Skills.` : "Loading…"}
        columns={[
          {
            header: "Skill",
            primary: true,
            cell: (s) => (
              <>
                <span className="font-mono text-sm">{s.name}</span>
                <div className="mt-0.5 truncate text-sm text-muted">{s.description}</div>
              </>
            ),
          },
          { header: "Version", cell: (s) => <Badge>v{s.version}</Badge> },
          { header: "Used by", cell: (s) => <SkillWho skill={s} agents={agents} /> },
          { header: "Updated", className: "text-xs text-muted", cell: (s) => `Updated ${timeAgo(s.updatedAt)}` },
        ]}
      />
      <ErrorText error={error} />
    </div>
  );
}

function SubAgentsTab({ data, connectors }: { data: AgentInsightsDTO; connectors: ConnectorDTO[] }) {
  const [creating, setCreating] = useState(false);
  const { agent } = data;
  return (
    <div className="space-y-6">
      {data.parent && (
        <List title="Parent agent">
          <ListRow
            to={`/admin/agents/${data.parent.id}`}
            leading={<AgentAvatar agent={data.parent.id} name={data.parent.name} />}
            title={<span className="truncate text-sm font-medium">{data.parent.name}</span>}
            subtitle={<span className="text-xs">{agent.name} works with {data.parent.name}'s connectors, and its prompt names it as its parent.</span>}
          />
        </List>
      )}
      <List
        title={`Sub-agents · ${data.subAgents.length}`}
        action={!agent.isPrimary && <Button onClick={() => setCreating(true)}>New sub-agent</Button>}
        empty={
          agent.isPrimary ? (
            "Pandito hands work to specialists; sub-agents belong to a specialist."
          ) : (
            <>
              No sub-agents. {agent.name} can propose one with <code className="font-mono text-xs">pandas propose agent</code>, or create one here.
            </>
          )
        }
      >
        {!agent.isPrimary &&
          data.subAgents.map((a) => (
            <ListRow
              key={a.id}
              to={`/admin/agents/${a.id}`}
              leading={<Avatar name={a.name} src={a.avatarUrl} size="sm" />}
              title={
                <>
                  <span className="truncate text-sm font-medium">{a.name}</span>
                  {!a.enabled && <Badge tone="amber">disabled</Badge>}
                </>
              }
              subtitle={a.description || "No description"}
            />
          ))}
      </List>
      {creating && (
        <NewAgentModal
          title={`New sub-agent of ${agent.name}`}
          connectors={connectors}
          preset={{ parentAgentId: agent.id, ownerMode: agent.ownerMode, owners: agent.owners, model: agent.model }}
          onClose={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            navigate(`/admin/agents/${a.id}`);
          }}
        />
      )}
    </div>
  );
}

/** Every edition of the instructions and model, newest first. Restoring an old one appends a copy of it. */
function VersionsList({ agent, onRestored }: { agent: AgentDTO; onRestored: () => void }) {
  const [versions, setVersions] = useState<AgentVersionDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    get<AgentVersionDTO[]>(`/api/agents/${agent.id}/versions`).then(setVersions, (e) => setError(e.message));
  }, [agent.id, agent.currentVersionId]);
  return (
    <List
      title="Versions"
      hint={`Saving other instructions or a model makes a new version; every run records the one it used.${agent.youAnswerFor === false ? " Restoring one is for its owners." : ""}`}
      empty="No versions yet."
      footer={error && <ErrorText error={error} />}
    >
      {versions.map((v) => (
        <ListRow
          key={v.id}
          title={<span className="font-mono text-xs">v{v.version}</span>}
          subtitle={`${modelLabel(v.model)} · ${v.createdBy} · ${timeAgo(v.createdAt)}`}
          trailing={
            v.id === agent.currentVersionId ? (
              <Badge tone="emerald">Current</Badge>
            ) : agent.youAnswerFor === false ? null : (
              <Button size="sm" onClick={() => post(`/api/agents/${agent.id}/versions/${v.version}/restore`).then(onRestored, (e) => setError(e.message))}>
                Restore
              </Button>
            )
          }
        />
      ))}
    </List>
  );
}

/** The connectors a run of this agent reaches, and the level its own runs get on each. */
/** "Read-only — default level", or just "Read and write" when the level is named for what it is. */
function levelWords(c: ConnectorDTO, level: string): string {
  const named = c.levels.find((l) => l.key === level);
  const character = accessCharacter(named?.policy ?? c.policy);
  const name = level === "default" || !named ? "default level" : named.name;
  return name.toLowerCase() === character.toLowerCase() ? character : `${character} — ${name}`;
}

function ConnectorsTab({ data, agentConnectors, onChanged }: { data: AgentInsightsDTO; agentConnectors: ConnectorDTO[]; onChanged: () => void }) {
  const { agent } = data;
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Runs one write, with its row busy and any error shown. */
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const setLevel = (c: ConnectorDTO, level: string) => run(c.id, async () => {
    await put(`/api/agents/${agent.id}/connectors/${c.id}`, { level });
    onChanged();
  });

  return (
    <List
      title="Connectors"
      hint={
        <>
          The level a run on this agent's own behalf gets: its own schedules, triggers, wakes and API calls, which use the workspace key. A person's runs, their schedules included, keep the person's level and login.
          {agent.isPrimary && " The primary agent reaches all connectors but those that need an agent's own grant, at their default level for runs on its own behalf."}
        </>
      }
      action={!agent.canDelegate && <Badge tone="amber">Delegation: off</Badge>}
      empty="No connectors granted."
      footer={error && <ErrorText error={error} />}
    >
      {agentConnectors.map((c) => {
        const own = agent.connectorIds.includes(c.id);
        const level = agent.connectorLevels[c.id] ?? "default";
        return (
          <ListRow
            key={c.id}
            leading={
              <span className="grid size-8 place-items-center rounded-full bg-raised shadow-sm ring-1 ring-line">
                <BrandIcon brand={connectorBrand(c)} className="size-4" />
              </span>
            }
            title={
              <>
                <span className="truncate text-sm font-medium">{c.name}</span>
                <span className="font-mono text-xs text-muted">{c.slug}</span>
              </>
            }
            subtitle={
              <span className="text-xs">
                {agent.isPrimary && !c.agentGrantRequired ? "All connectors · default level for runs on its own behalf" : own ? levelWords(c, level) : `level from ${data.parent?.name ?? "the parent"}`}
                {" · "}
                {c.credentials === "internal" ? "connected through Pandas" : c.workspaceMode === "signin_app" ? "its own runs use the sign-in app itself, read only" : c.hasSecrets ? "its own runs use the workspace key" : "no workspace key: its own runs cannot use it"}
                {c.credentials === "own" && " · people use their own login"}
              </span>
            }
            trailing={
              own && !agent.isPrimary && c.levels.length > 0 && (
                <select
                  aria-label={`Level for ${c.name}`}
                  className={`${inputCls} w-52 max-w-full py-1.5 text-xs`}
                  value={level}
                  disabled={busy === c.id}
                  onChange={(e) => void setLevel(c, e.target.value)}
                >
                  <option value="default">Default level</option>
                  {c.levels.map((l) => (
                    <option key={l.key} value={l.key}>{l.name}</option>
                  ))}
                </select>
              )
            }
          />
        );
      })}
    </List>
  );
}

/**
 * The agent's picture, set on its own rather than with the form: a PNG, JPEG or WebP up to 1 MB. Without one it
 * shows its initials, and Pandito shows the panda. The URL says where the picture comes from, so it also says
 * whether there is one of its own to remove.
 */
function AvatarField({ agent, onSaved }: { agent: AgentDTO; onSaved: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploaded = agent.avatarUrl?.startsWith("/api/agents/") ?? false;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      forgetAgentAvatars();
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-line pb-4">
      {agent.avatarUrl ? (
        <img src={agent.avatarUrl} alt="" className="size-16 shrink-0 rounded-2xl object-cover shadow-sm ring-1 ring-line" />
      ) : (
        <Avatar name={agent.name} size="lg" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-muted">Avatar</div>
        <p className="mt-0.5 text-xs text-muted">
          A PNG, JPEG or WebP image, up to 1 MB. Shown wherever {agent.name} appears.
          {uploaded ? "" : agent.isPrimary ? " Without one it keeps the panda." : " Without one it shows its initials."}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void run(() => postFile(`/api/agents/${agent.id}/avatar`, file));
            }}
          />
          <Button disabled={busy} onClick={() => input.current?.click()}>
            {busy ? "Working…" : uploaded ? "Replace image" : "Upload image"}
          </Button>
          {uploaded && (
            <Button variant="ghost" disabled={busy} onClick={() => void run(() => del(`/api/agents/${agent.id}/avatar`))}>
              Remove
            </Button>
          )}
        </div>
        <div className="mt-2">
          <ErrorText error={error} />
        </div>
      </div>
    </div>
  );
}

const ARCHIVE_AGENT_BODY = "Its runs and history are kept. It is switched off, hidden from every list, and can't be woken, chatted with or handed work. Its schedules, wakes and triggers stop, and anything waiting to run is dropped. You can unarchive it, but those stay off.";

function SettingsTab({ data, connectors, onSaved }: { data: AgentInsightsDTO; connectors: ConnectorDTO[]; onSaved: () => void }) {
  const [draft, setDraft] = useState<AgentDraft>(toDraft(data.agent));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { users, agents } = usePickers();
  return (
    <div className="max-w-3xl space-y-6">
      <div className="card space-y-4 p-5">
        <AvatarField agent={data.agent} onSaved={onSaved} />
        <AgentForm draft={draft} connectors={connectors} onChange={setDraft} users={users} agents={agents} />
        <ErrorText error={error} />
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
          <Button
            variant="primary"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              setError(null);
              try {
                await saveAgent(draft);
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
                onSaved();
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? "Saving…" : saved ? "Saved" : "Save changes"}
          </Button>
          <PromptPreview agent={data.agent} />
          {!data.agent.isPrimary && (
            <div className="ml-auto flex flex-wrap gap-2">
              {/* What has run is archived: kept with its history, switched off and hidden from every list. */}
              <Button
                onClick={async () => {
                  setError(null);
                  try {
                    if (data.agent.archivedAt) {
                      await post(`/api/agents/${data.agent.id}/unarchive`);
                    } else {
                      if (!(await confirmDialog({ title: `Archive ${data.agent.name}?`, body: ARCHIVE_AGENT_BODY, confirm: "Archive" }))) return;
                      await post(`/api/agents/${data.agent.id}/archive`);
                    }
                    onSaved();
                  } catch (err) {
                    setError((err as Error).message);
                  }
                }}
              >
                {data.agent.archivedAt ? "Unarchive" : "Archive"}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setError(null);
                  try {
                    if (await deleteAgent(data.agent)) navigate("/admin/agents");
                  } catch (err) {
                    // It has run: say so, and offer the way that keeps its history.
                    if (!(err instanceof ApiError) || err.code !== "has_runs" || data.agent.archivedAt) return setError((err as Error).message);
                    const offer = await confirmDialog({ title: `${data.agent.name} can't be deleted`, body: `${err.message.replace(/: archive it instead$/, "")}. Archive it instead: ${ARCHIVE_AGENT_BODY}`, confirm: "Archive instead" });
                    if (offer) {
                      await post(`/api/agents/${data.agent.id}/archive`).catch((e) => setError((e as Error).message));
                      onSaved();
                    }
                  }
                }}
              >
                Delete agent
              </Button>
            </div>
          )}
        </div>
      </div>
      <AgentModelList agentId={data.agent.id} />
      <VersionsList agent={data.agent} onRestored={onSaved} />
    </div>
  );
}

/** One agent, full screen: activity and spend, delegations, tools, memory, schedules and settings. */
export function AdminAgentDetail({ agentId }: { agentId: string }) {
  const [data, setData] = useState<AgentInsightsDTO | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [skills, setSkills] = useState<SkillDTO[] | null>(null);
  const [skillsError, setSkillsError] = useState<string | null>(null);
  const { types } = useScopeTypes();
  const [error, setError] = useState<string | null>(null);
  // A link can open a tab directly: "open the goal" on a decided proposal lands on this agent's goal.
  const [tab, setTab] = useTabParam(TABS);

  const load = () => get<AgentInsightsDTO>(`/api/agents/${agentId}/insights?days=30`).then(setData, (e) => setError(e.message));
  useEffect(() => {
    setData(null);
    void load();
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
    setSkills(null);
    setSkillsError(null);
    get<SkillDTO[]>(`/api/skills?level=agent&agentId=${agentId}`).then(setSkills, (e) => setSkillsError(e.message));
    const timer = setInterval(() => void load(), 20_000);
    return () => clearInterval(timer);
  }, [agentId]);

  if (!data) {
    return (
      <div className="space-y-4 px-4 py-8 md:px-6">
        {error ? <ErrorText error={error} /> : <div className="card h-40 animate-pulse" />}
      </div>
    );
  }
  const { agent, stats } = data;
  const successRate = stats.runs ? Math.round((stats.succeeded / stats.runs) * 100) : null;
  // A sub-agent reaches its parent's connectors too, so show those as its own.
  const inheritedIds = data.parent?.connectorIds ?? [];
  const agentConnectors = connectors.filter(
    (c) => c.enabled && ((agent.isPrimary && !c.agentGrantRequired) || agent.connectorIds.includes(c.id) || (inheritedIds.includes(c.id) && !c.agentGrantRequired)),
  );
  const inheritedCount = agentConnectors.filter((c) => !agent.connectorIds.includes(c.id)).length;
  const pendingRequests = data.approvals.filter((a) => a.status === "pending").length;
  const tabs: TabItem<Tab>[] = [
    { key: "activity", label: "Activity" },
    { key: "goals", label: "Goal" },
    { key: "runs", label: "Runs" },
    { key: "requests", label: "Requests", count: pendingRequests || undefined },
    { key: "connectors", label: "Connectors", count: agentConnectors.length },
    { key: "skills", label: "Skills", count: skills?.length },
    // The primary agent works for people, never under a scope. An agent's one type names the tab: Accounts.
    ...(agent.isPrimary ? [] : [{ key: "scopes", label: scopesLabel(agent, types) } as TabItem<Tab>]),
    { key: "memory", label: "Memory", count: data.memories.length },
    { key: "schedules", label: "Schedules", count: data.schedules.length },
    { key: "triggers", label: "Triggers" },
    { key: "subagents", label: "Sub-agents", count: data.subAgents.length },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div className="pb-12">
      <div className="px-4 pt-5 md:px-6">
        <Link to="/admin/agents" className="text-sm text-muted hover:text-fg">← Agents</Link>
      </div>
      <div className="flex flex-col gap-4 px-4 pt-4 pb-5 sm:flex-row sm:items-center md:px-6">
        {agent.avatarUrl ? (
          <img src={agent.avatarUrl} alt="" className="size-16 shrink-0 rounded-2xl object-cover shadow-sm ring-1 ring-line" />
        ) : (
          <Avatar name={agent.name} size="lg" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-title font-semibold tracking-tight">{agent.name}</h1>
            <Badge>v{agent.version}</Badge>
            {agent.isPrimary && <Badge tone="blue">Primary agent</Badge>}
            {agent.archivedAt && <Badge tone="amber">Archived</Badge>}
            <AgentStatusChip status={data.status} nextRunAt={data.nextRunAt} />
          </div>
          <p className="mt-1 max-w-3xl text-sm text-muted">{agent.description || "No description"}</p>
          <p className="mt-0.5 text-xs text-faint">{agentStatusWords(data.status, data.nextRunAt).hint}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
            <span className="font-mono">{agent.slug}</span>
            <span>{modelLabel(agent.model)}</span>
            {data.parent && (
              <TextLink to={`/admin/agents/${data.parent.id}`}>
                Sub-agent of {data.parent.name}
              </TextLink>
            )}
            <span>{ownersLine(agent)}</span>
            <span>{accessLine(agent)}</span>
            {agent.monthlyBudgetUsd !== null && (
              <span className={data.monthSpendUsd >= agent.monthlyBudgetUsd ? "font-medium text-danger" : ""}>
                Budget {money(data.monthSpendUsd)} of {money(agent.monthlyBudgetUsd)}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="flex -space-x-1">
                {agentConnectors.slice(0, 8).map((c) => (
                  <span key={c.id} title={c.name} className="grid size-5 place-items-center rounded-full bg-raised shadow-sm ring-2 ring-canvas">
                    <BrandIcon brand={connectorBrand(c)} className="size-3" />
                  </span>
                ))}
              </span>
              {agent.isPrimary
                ? "All connectors"
                : `${agentConnectors.length} connectors${inheritedCount > 0 ? ` (${inheritedCount} from ${data.parent!.name})` : ""}`}
            </span>
          </div>
          <ErrorText error={switchError} />
        </div>
        {/* The agent's own on/off, on every tab: it saves the moment it is flipped. */}
        <Switch
          label={agent.enabled ? "On" : "Off"}
          className="shrink-0 self-start sm:self-center"
          checked={agent.enabled}
          disabled={agent.isPrimary}
          title={agent.isPrimary ? "The primary agent always stays on" : undefined}
          onChange={(enabled) => {
            setSwitchError(null);
            patch(`/api/agents/${agent.id}`, { enabled }).then(load, (e) => setSwitchError((e as Error).message));
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 sm:grid-cols-3 md:px-6 xl:grid-cols-6">
        <StatCard label="Runs · 30 days" value={stats.runs} trend={data.daily.map((d) => d.runs)} />
        <StatCard label="Spend · 30 days" value={money(stats.costUsd)} trend={data.daily.map((d) => d.costUsd)} tone="text-emerald-300" breakdown={stats} />
        <StatCard label="Spend this month" value={money(stats.costMonthUsd)} breakdown={stats.month} />
        <StatCard label="Succeeded" value={successRate === null ? "—" : `${successRate}%`} />
        <StatCard label="Average run" value={stats.avgDurationMs === null ? "—" : duration(new Date(0).toISOString(), new Date(stats.avgDurationMs).toISOString())} />
        <StatCard label="Tokens in / out" value={`${compact(stats.inputTokens)} / ${compact(stats.outputTokens)}`} />
      </div>

      <div className="mt-6 px-4 md:px-6">
        <Tabs label="Section" tabs={tabs} value={tab} onChange={setTab} className="mb-5" />
        {tab === "activity" && <ActivityTab data={data} connectors={connectors} />}
        {tab === "goals" && <GoalTab data={data} onChanged={load} />}
        {tab === "runs" && <RunsList key={agent.id} filter={{ agentId: agent.id }} show="scope" hint={`Every run of ${agent.name}, across all its scopes. Open one to see each step it took.`} />}
        {tab === "requests" && <RequestsTab agent={agent} />}
        {tab === "connectors" && <ConnectorsTab data={data} agentConnectors={agentConnectors} onChanged={load} />}
        {tab === "skills" && <SkillsTab agent={agent} skills={skills} error={skillsError} />}
        {tab === "subagents" && <SubAgentsTab data={data} connectors={connectors} />}
        {tab === "scopes" && <AgentScopes key={agent.id} agent={agent} />}
        {tab === "memory" && <MemoryTab key={data.agent.id} data={data} onChanged={load} />}
        {tab === "schedules" && <SchedulesTab data={data} onChanged={load} />}
        {tab === "triggers" && <AgentTriggers key={agent.id} agent={agent} />}
        {tab === "settings" && <SettingsTab key={agent.updatedAt} data={data} connectors={connectors} onSaved={load} />}
      </div>
    </div>
  );
}

/** The scopes tab is named after the agent's one scope type, in the plural ("Accounts"); "Scopes" while it has none. */
function scopesLabel(agent: AgentDTO, types: SubjectTypeDTO[] | null | undefined): string {
  const type = types?.find((t) => t.key === agent.scopeTypes[0]);
  return type ? pluralWord(type.name) : "Scopes";
}
